// frontend/src/components/CustomerBooking.js - Redesigned similar to Admin Quick Book
import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';

const CustomerBooking = () => {
  const [sports, setSports] = useState([]);
  const [courts, setCourts] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [selectedSport, setSelectedSport] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [availability, setAvailability] = useState({});
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [branding, setBranding] = useState(null);

  const [customerData, setCustomerData] = useState({
    name: '',
    phone: '',
    email: ''
  });

  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 6; hour < 22; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const startHour = hour.toString().padStart(2, '0');
        const startMinute = minute.toString().padStart(2, '0');
        const endHour = minute === 30 ? (hour + 1).toString().padStart(2, '0') : hour.toString().padStart(2, '0');
        const endMinute = minute === 30 ? '00' : '30';

        const startTime = `${startHour}:${startMinute}:00`;
        const endTime = `${endHour}:${endMinute}:00`;
        const displayTime = `${startHour}:${startMinute}`;

        slots.push({ startTime, endTime, displayTime, id: `${startTime}-${endTime}` });
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const calculateSlotPrice = (courtId, time) => {
    const pricing = pricingRules.find(p => p.court_id === courtId);
    if (!pricing || !pricing.time_slots) return 150;

    const date = new Date(selectedDate);
    const dayOfWeek = date.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const hour = parseInt(time.split(':')[0]);

    const slots = isWeekend ? pricing.time_slots.weekend : pricing.time_slots.weekday;
    let hourlyPrice = 0;

    if (isWeekend) {
      if (hour >= 6 && hour < 18) {
        hourlyPrice = slots['06:00-18:00'] || 300;
      } else if (hour >= 18 && hour < 22) {
        hourlyPrice = slots['18:00-22:00'] || 300;
      }
    } else {
      if (hour >= 6 && hour < 11) {
        hourlyPrice = slots['06:00-11:00'] || 300;
      } else if (hour >= 11 && hour < 16) {
        hourlyPrice = slots['11:00-16:00'] || 300;
      } else if (hour >= 16 && hour < 18) {
        hourlyPrice = slots['16:00-18:00'] || 300;
      } else if (hour >= 18 && hour < 22) {
        hourlyPrice = slots['18:00-22:00'] || 300;
      }
    }

    return hourlyPrice / 2; // 30-min slot = half hourly rate
  };

  const fetchBranding = async () => {
    try {
      const response = await fetch(`${API_URL}/branding/public`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.settings) {
          setBranding(data.settings);
        }
      }
    } catch (error) {
      console.log('Using default branding');
    }
  };

  const fetchPricingRules = async () => {
    try {
      const response = await fetch(`${API_URL}/pricing`);
      if (response.ok) {
        const data = await response.json();
        setPricingRules(data);
      }
    } catch (error) {
      console.error('Error fetching pricing rules:', error);
    }
  };

  const fetchCourts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/courts`);

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      let courtsData = [];

      if (data.success && data.courts) {
        courtsData = data.courts;
      } else if (data.data) {
        courtsData = data.data;
      } else if (Array.isArray(data)) {
        courtsData = data;
      }

      setCourts(courtsData);

      const uniqueSports = [...new Set(courtsData.map(c => c.sport_type || c.sport_name))].filter(Boolean);
      setSports(uniqueSports);

      setError('');
    } catch (error) {
      console.error('Error fetching courts:', error);
      setError('Failed to connect to server.');
    } finally {
      setLoading(false);
    }
  };

  const checkAvailability = async (sport, date) => {
    if (!sport || !date) return;

    const filteredCourts = courts.filter(c => (c.sport_type || c.sport_name) === sport);
    if (filteredCourts.length === 0) return;

    try {
      setLoading(true);
      const availabilityMap = {};

      for (const court of filteredCourts) {
        const response = await fetch(`${API_URL}/bookings/availability/${court.id}/${date}`);

        if (response.ok) {
          const existingBookings = await response.json();

          timeSlots.forEach(slot => {
            const key = `${court.id}-${slot.id}`;
            const booking = existingBookings.find(b =>
              slot.startTime < b.end_time && slot.endTime > b.start_time
            );

            availabilityMap[key] = booking ? { available: false } : { available: true };
          });
        } else {
          timeSlots.forEach(slot => {
            availabilityMap[`${court.id}-${slot.id}`] = { available: true };
          });
        }
      }

      setAvailability(availabilityMap);
    } catch (error) {
      console.error('Error checking availability:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSportSelect = (sport) => {
    setSelectedSport(sport);
    setSelectedSlots([]);
    setShowBookingForm(false);
    setError('');
    setSuccess('');
    if (selectedDate) {
      checkAvailability(sport, selectedDate);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedSlots([]);
    setShowBookingForm(false);
    setError('');
    setSuccess('');
    if (selectedSport) {
      checkAvailability(selectedSport, date);
    }
  };

  const handleSlotClick = (court, slot, slotData) => {
    if (!slotData.available) return;

    const now = new Date();
    const slotDateTime = new Date(`${selectedDate}T${slot.startTime}`);

    if (slotDateTime < now) {
      alert('Cannot book past time slots');
      return;
    }

    const existingIndex = selectedSlots.findIndex(s =>
      s.court.id === court.id && s.slot.id === slot.id
    );

    if (existingIndex >= 0) {
      setSelectedSlots(selectedSlots.filter((_, i) => i !== existingIndex));
    } else {
      const price = calculateSlotPrice(court.id, slot.startTime);
      setSelectedSlots([...selectedSlots, { court, slot, price }]);
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();

    if (selectedSlots.length === 0 || !customerData.name || !customerData.phone) {
      setError('Please select slots and fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError('');

      let successCount = 0;
      const references = [];

      for (const { court, slot, price } of selectedSlots) {
        try {
          const response = await fetch(`${API_URL}/bookings/customer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              court_id: court.id,
              booking_date: selectedDate,
              start_time: slot.startTime,
              end_time: slot.endTime,
              customer_name: customerData.name,
              customer_phone: customerData.phone,
              customer_email: customerData.email || null,
              booking_source: 'online',
              total_amount: price
            })
          });

          const data = await response.json();

          if (data.success || data.id) {
            successCount++;
            if (data.reference) references.push(data.reference);
          }
        } catch (err) {
          console.error('Error creating booking for slot:', slot, err);
        }
      }

      if (successCount > 0) {
        setSuccess(`Booking confirmed! ${successCount} slot${successCount > 1 ? 's' : ''} booked.${references.length > 0 ? ` Reference: ${references.join(', ')}` : ''}`);
        setShowBookingForm(false);
        setCustomerData({ name: '', phone: '', email: '' });
        setSelectedSlots([]);
        checkAvailability(selectedSport, selectedDate);
      } else {
        setError('Failed to create booking. Slots may already be booked.');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      setError('Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourts();
    fetchPricingRules();
    fetchBranding();
  }, []);

  const getMinDate = () => new Date().toISOString().split('T')[0];
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    return maxDate.toISOString().split('T')[0];
  };

  const filteredCourts = courts.filter(c => (c.sport_type || c.sport_name) === selectedSport);
  const totalAmount = selectedSlots.reduce((sum, s) => sum + s.price, 0);
  const facilityName = branding?.centerName || 'Sports Facility';

  if (loading && !courts.length) {
    return (
      <div className="public-loading">
        <div className="public-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="public-booking">
      {/* Header */}
      <div className="public-header">
        <h1>{facilityName}</h1>
        <p>Book your court online</p>
      </div>

      <div className="public-content">
        {/* Alerts */}
        {success && (
          <div className="public-alert success">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {success}
          </div>
        )}

        {error && (
          <div className="public-alert error">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {/* Selection Panel */}
        <div className="public-panel">
          <div className="public-filters">
            <div className="filter-group">
              <label>Select Sport</label>
              <select
                value={selectedSport}
                onChange={(e) => handleSportSelect(e.target.value)}
              >
                <option value="">Choose a sport</option>
                {sports.map(sport => (
                  <option key={sport} value={sport}>{sport}</option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Select Date</label>
              <input
                type="date"
                value={selectedDate}
                min={getMinDate()}
                max={getMaxDate()}
                onChange={(e) => handleDateSelect(e.target.value)}
              />
            </div>

            {selectedSlots.length > 0 && (
              <div className="filter-group selection-info">
                <span className="slot-count">{selectedSlots.length} slot{selectedSlots.length > 1 ? 's' : ''} = <strong>₹{totalAmount}</strong></span>
                <button onClick={() => setShowBookingForm(true)} className="proceed-btn">
                  Book Now
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Availability Grid */}
        {selectedSport && selectedDate && filteredCourts.length > 0 && (
          <div className="public-panel">
            <div className="grid-header">
              <h3>Select Time Slots</h3>
              <div className="grid-legend">
                <span className="legend-item available">Available</span>
                <span className="legend-item selected">Selected</span>
                <span className="legend-item booked">Booked</span>
              </div>
            </div>

            {loading ? (
              <div className="grid-loading">
                <div className="public-spinner small"></div>
                <span>Checking availability...</span>
              </div>
            ) : (
              <div className="availability-grid">
                <table>
                  <thead>
                    <tr>
                      <th className="time-col">Time</th>
                      {filteredCourts.map(court => (
                        <th key={court.id}>{court.court_name || court.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map(slot => {
                      const now = new Date();
                      const slotDateTime = new Date(`${selectedDate}T${slot.startTime}`);
                      const isPast = slotDateTime < now;

                      return (
                        <tr key={slot.id}>
                          <td className="time-col">{slot.displayTime}</td>
                          {filteredCourts.map(court => {
                            const key = `${court.id}-${slot.id}`;
                            const slotData = availability[key] || { available: true };
                            const isSelected = selectedSlots.some(s =>
                              s.court.id === court.id && s.slot.id === slot.id
                            );
                            const price = calculateSlotPrice(court.id, slot.startTime);

                            let cellClass = 'slot-cell';
                            if (isPast) cellClass += ' past';
                            else if (isSelected) cellClass += ' selected';
                            else if (slotData.available) cellClass += ' available';
                            else cellClass += ' booked';

                            return (
                              <td key={key}>
                                <button
                                  onClick={() => handleSlotClick(court, slot, slotData)}
                                  disabled={!slotData.available || isPast}
                                  className={cellClass}
                                >
                                  {isPast ? '—' : isSelected ? `✓ ₹${price}` : slotData.available ? `₹${price}` : 'Booked'}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!selectedSport && !selectedDate && (
          <div className="public-panel empty-state">
            <div className="empty-icon">🏸</div>
            <h3>Ready to play?</h3>
            <p>Select a sport and date above to see available time slots</p>
          </div>
        )}

        {/* Booking Form Modal */}
        {showBookingForm && selectedSlots.length > 0 && (
          <div className="booking-modal-overlay" onClick={() => setShowBookingForm(false)}>
            <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Complete Your Booking</h2>
                <button className="close-btn" onClick={() => setShowBookingForm(false)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="modal-body">
                {/* Selected Slots Summary */}
                <div className="slots-summary">
                  <h4>Selected Slots ({selectedSlots.length})</h4>
                  <div className="slots-list">
                    {selectedSlots.map(({ court, slot, price }, idx) => (
                      <div key={idx} className="slot-item">
                        <span className="slot-court">{court.court_name || court.name}</span>
                        <span className="slot-time">{slot.displayTime}</span>
                        <span className="slot-price">₹{price}</span>
                      </div>
                    ))}
                  </div>
                  <div className="slots-total">
                    <span>Total Amount</span>
                    <span className="total-value">₹{totalAmount}</span>
                  </div>
                  {/* Payment Options */}
                  <div className="payment-options">
                    <div className="payment-option venue">
                      <svg viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span>Pay at venue (Cash/Card)</span>
                    </div>

                    {branding?.upiId && (
                      <div className="payment-option upi">
                        <div className="upi-header">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                          </svg>
                          <span>Pay via UPI / GPay</span>
                        </div>
                        <div className="upi-details">
                          <div className="upi-id">
                            <strong>UPI ID:</strong> {branding.upiId}
                          </div>
                          {branding.upiQrUrl && (
                            <div className="upi-qr">
                              <img src={branding.upiQrUrl} alt="UPI QR Code" />
                              <span>Scan to pay ₹{totalAmount}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Customer Form */}
                <form onSubmit={submitBooking} className="customer-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Your Name *</label>
                      <input
                        type="text"
                        value={customerData.name}
                        onChange={(e) => setCustomerData({ ...customerData, name: e.target.value })}
                        required
                        placeholder="Enter your name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone Number *</label>
                      <input
                        type="tel"
                        value={customerData.phone}
                        onChange={(e) => setCustomerData({ ...customerData, phone: e.target.value })}
                        required
                        placeholder="Enter phone number"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Email (Optional)</label>
                    <input
                      type="email"
                      value={customerData.email}
                      onChange={(e) => setCustomerData({ ...customerData, email: e.target.value })}
                      placeholder="Enter email for confirmation"
                    />
                  </div>

                  <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={() => setShowBookingForm(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn-confirm" disabled={loading}>
                      {loading ? 'Processing...' : `Confirm Booking`}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .public-booking {
          min-height: 100vh;
          background: #f5f7fa;
        }

        .public-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 40px 24px;
          text-align: center;
        }

        .public-header h1 {
          margin: 0 0 8px;
          font-size: 1.75rem;
          font-weight: 700;
        }

        .public-header p {
          margin: 0;
          opacity: 0.9;
          font-size: 1rem;
        }

        .public-content {
          max-width: 1100px;
          margin: 0 auto;
          padding: 24px 16px;
        }

        .public-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 100vh;
          color: #6b7280;
        }

        .public-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e5e7eb;
          border-top-color: #667eea;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 12px;
        }

        .public-spinner.small {
          width: 24px;
          height: 24px;
          border-width: 2px;
          margin-bottom: 0;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Alerts */
        .public-alert {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          border-radius: 10px;
          margin-bottom: 20px;
          font-weight: 500;
        }

        .public-alert svg {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
        }

        .public-alert.success {
          background: #ecfdf5;
          color: #065f46;
          border: 1px solid #a7f3d0;
        }

        .public-alert.error {
          background: #fef2f2;
          color: #991b1b;
          border: 1px solid #fecaca;
        }

        /* Panel */
        .public-panel {
          background: white;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          padding: 20px;
          margin-bottom: 20px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }

        /* Filters */
        .public-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          align-items: flex-end;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .filter-group label {
          font-size: 12px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .filter-group select,
        .filter-group input[type="date"] {
          padding: 10px 14px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 15px;
          font-family: inherit;
          min-width: 180px;
          height: 44px;
          transition: all 0.15s;
          background: white;
        }

        .filter-group select:focus,
        .filter-group input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
        }

        /* Selection Info */
        .selection-info {
          flex-direction: row !important;
          align-items: center !important;
          gap: 12px !important;
          margin-left: auto;
          padding: 8px 14px;
          background: #ecfdf5;
          border-radius: 8px;
          border: 1px solid #a7f3d0;
        }

        .slot-count {
          font-size: 14px;
          color: #065f46;
        }

        .proceed-btn {
          padding: 10px 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
        }

        .proceed-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .empty-state h3 {
          margin: 0 0 8px;
          font-size: 1.25rem;
          color: #1f2937;
        }

        .empty-state p {
          margin: 0;
          color: #6b7280;
        }

        /* Grid */
        .grid-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
          flex-wrap: wrap;
          gap: 12px;
        }

        .grid-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
        }

        .grid-legend {
          display: flex;
          gap: 16px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: #6b7280;
        }

        .legend-item::before {
          content: '';
          width: 14px;
          height: 14px;
          border-radius: 4px;
        }

        .legend-item.available::before {
          background: #10b981;
        }

        .legend-item.selected::before {
          background: #667eea;
        }

        .legend-item.booked::before {
          background: #e5e7eb;
        }

        .grid-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 40px;
          color: #6b7280;
        }

        .availability-grid {
          overflow: auto;
          border-radius: 8px;
          border: 1px solid #e5e7eb;
          max-height: 450px;
        }

        .availability-grid table {
          width: 100%;
          border-collapse: collapse;
          min-width: 400px;
        }

        .availability-grid th,
        .availability-grid td {
          padding: 0;
          border-bottom: 1px solid #f3f4f6;
        }

        .availability-grid th {
          position: sticky;
          top: 0;
          padding: 10px 4px;
          background: #f9fafb;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          color: #6b7280;
          text-align: center;
          z-index: 1;
        }

        .availability-grid th.time-col,
        .availability-grid td.time-col {
          position: sticky;
          left: 0;
          text-align: left;
          padding-left: 12px;
          width: 70px;
          background: #fafbfc;
          font-weight: 600;
          font-size: 13px;
          color: #374151;
          z-index: 2;
        }

        .availability-grid th.time-col {
          z-index: 3;
        }

        .availability-grid tbody tr:last-child td {
          border-bottom: none;
        }

        /* Slot Cells */
        .slot-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          width: calc(100% - 4px);
          padding: 6px 4px;
          border: none;
          background: transparent;
          cursor: pointer;
          transition: all 0.15s;
          min-height: 32px;
          border-radius: 4px;
          margin: 2px;
          font-size: 12px;
          font-weight: 500;
        }

        .slot-cell.available {
          background: #d1fae5;
          color: #065f46;
        }

        .slot-cell.available:hover {
          background: #a7f3d0;
          transform: scale(1.02);
        }

        .slot-cell.selected {
          background: #667eea;
          color: white;
        }

        .slot-cell.booked {
          background: #f3f4f6;
          color: #9ca3af;
          cursor: not-allowed;
        }

        .slot-cell.past {
          background: #f9fafb;
          color: #d1d5db;
          cursor: not-allowed;
        }

        /* Modal */
        .booking-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .booking-modal {
          background: white;
          border-radius: 16px;
          width: 100%;
          max-width: 480px;
          max-height: 90vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 24px;
          border-bottom: 1px solid #e5e7eb;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.2rem;
          font-weight: 700;
        }

        .close-btn {
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: rgba(255,255,255,0.2);
          border-radius: 8px;
          cursor: pointer;
          color: white;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: rgba(255,255,255,0.3);
        }

        .close-btn svg {
          width: 20px;
          height: 20px;
        }

        .modal-body {
          padding: 24px;
          overflow-y: auto;
        }

        /* Slots Summary */
        .slots-summary {
          background: #f9fafb;
          border-radius: 10px;
          padding: 16px;
          margin-bottom: 24px;
        }

        .slots-summary h4 {
          margin: 0 0 12px;
          font-size: 13px;
          font-weight: 600;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.02em;
        }

        .slots-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          max-height: 120px;
          overflow-y: auto;
        }

        .slot-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          background: white;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }

        .slot-court {
          font-weight: 600;
          color: #1f2937;
          font-size: 14px;
        }

        .slot-time {
          color: #6b7280;
          font-size: 13px;
        }

        .slot-item .slot-price {
          color: #667eea;
          font-weight: 600;
        }

        .slots-total {
          display: flex;
          justify-content: space-between;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 2px solid #e5e7eb;
          font-weight: 600;
          color: #1f2937;
        }

        .total-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #667eea;
        }

        /* Payment Options */
        .payment-options {
          margin-top: 16px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .payment-option {
          padding: 12px;
          border-radius: 8px;
          font-size: 13px;
        }

        .payment-option.venue {
          display: flex;
          align-items: center;
          gap: 10px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #166534;
        }

        .payment-option.venue svg {
          width: 20px;
          height: 20px;
        }

        .payment-option.upi {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
        }

        .upi-header {
          display: flex;
          align-items: center;
          gap: 10px;
          color: #1e40af;
          font-weight: 600;
          margin-bottom: 10px;
        }

        .upi-header svg {
          width: 20px;
          height: 20px;
          color: #2563eb;
        }

        .upi-details {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .upi-id {
          font-size: 14px;
          color: #1e3a8a;
          background: white;
          padding: 8px 12px;
          border-radius: 6px;
          font-family: monospace;
        }

        .upi-qr {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: white;
          border-radius: 8px;
        }

        .upi-qr img {
          width: 150px;
          height: 150px;
          border-radius: 8px;
          border: 2px solid #e5e7eb;
        }

        .upi-qr span {
          font-size: 12px;
          color: #6b7280;
          font-weight: 500;
        }

        /* Form */
        .customer-form .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .customer-form .form-group {
          margin-bottom: 16px;
        }

        .customer-form label {
          display: block;
          margin-bottom: 6px;
          font-size: 14px;
          font-weight: 600;
          color: #374151;
        }

        .customer-form input {
          width: 100%;
          padding: 12px 14px;
          border: 2px solid #e5e7eb;
          border-radius: 8px;
          font-size: 15px;
          font-family: inherit;
          transition: all 0.2s;
        }

        .customer-form input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 8px;
        }

        .btn-cancel {
          flex: 1;
          padding: 14px;
          background: #f3f4f6;
          color: #374151;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #e5e7eb;
        }

        .btn-confirm {
          flex: 2;
          padding: 14px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-confirm:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
        }

        .btn-confirm:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        /* Responsive */
        @media (max-width: 640px) {
          .public-filters {
            flex-direction: column;
            align-items: stretch;
          }

          .filter-group {
            width: 100%;
          }

          .filter-group select,
          .filter-group input[type="date"] {
            width: 100%;
          }

          .selection-info {
            margin-left: 0;
            justify-content: space-between;
          }

          .customer-form .form-row {
            grid-template-columns: 1fr;
          }

          .grid-legend {
            display: none;
          }

          .form-actions {
            flex-direction: column;
          }

          .btn-cancel, .btn-confirm {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default CustomerBooking;
